import { format } from "date-fns";

/** Fallback display formats (date-fns) when platform config is missing. */
export const DISPLAY_DATE_FORMAT = "dd-MM-yyyy";
export const DISPLAY_TIME_FORMAT = "hh:mm a";

const readPlatformConfigFormat = (
  key: "date_format" | "time_format",
  fallback: string
): string => {
  try {
    const stored = JSON.parse(localStorage.getItem("config") || "{}");
    const fromApi = stored?.config?.[key];
    if (typeof fromApi === "string" && fromApi.trim() !== "") {
      return fromApi.trim();
    }
  } catch {
    // Invalid localStorage config — use default.
  }

  return fallback;
};

const readPlatformDateFormat = (): string =>
  readPlatformConfigFormat(
    "date_format",
    DISPLAY_DATE_FORMAT    
  );

const readPlatformTimeFormat = (): string =>
  readPlatformConfigFormat(
    "time_format",
    DISPLAY_TIME_FORMAT
  );

/** Convert date-fns tokens (dd-MM-yyyy) to Flatpickr tokens (d-m-Y). */
export const toFlatpickrDateFormat = (dateFnsFormat: string): string => {
  return dateFnsFormat.replace(/yyyy/g, "Y").replace(/dd/g, "d").replace(/MM/g, "m");
};

export const getDisplayDateFormat = (): string => readPlatformDateFormat();

/** Human-readable placeholder matching the configured display format (e.g. dd-mm-yyyy). */
export const getDisplayDatePlaceholder = (): string => getDisplayDateFormat().toLowerCase();

export const getDisplayTimeFormat = (): string => readPlatformTimeFormat();

export const getDisplayDateTimeFormat = (): string =>
  `${readPlatformDateFormat()} ${readPlatformTimeFormat()}`;

export const getFlatpickrDateFormat = (): string => toFlatpickrDateFormat(readPlatformDateFormat());

const pad = (value: number): string => value.toString().padStart(2, "0");

/**
 * Parse a date without UTC shift for date-only strings.
 * Accepts Date, ISO (yyyy-MM-dd), display (dd-MM-yyyy), and datetime strings.
 */
export const parseDate = (value?: string | Date | null): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const str = String(value).trim();
  if (!str) return null;

  const isoDateTime = str.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (isoDateTime) {
    const date = new Date(
      Number(isoDateTime[1]),
      Number(isoDateTime[2]) - 1,
      Number(isoDateTime[3]),
      Number(isoDateTime[4]),
      Number(isoDateTime[5]),
      Number(isoDateTime[6] || 0)
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const isoDate = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    const date = new Date(
      Number(isoDate[1]),
      Number(isoDate[2]) - 1,
      Number(isoDate[3])
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const displayDateTime = str.match(
    /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (displayDateTime) {
    const date = new Date(
      Number(displayDateTime[3]),
      Number(displayDateTime[2]) - 1,
      Number(displayDateTime[1]),
      Number(displayDateTime[4]),
      Number(displayDateTime[5]),
      Number(displayDateTime[6] || 0)
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const displayDate = str.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (displayDate) {
    const date = new Date(
      Number(displayDate[3]),
      Number(displayDate[2]) - 1,
      Number(displayDate[1])
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(str);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatToStorageDate = (date: Date): string => {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const shortDate = (date: string | Date): string => {
  const parsed = parseDate(date);
  return parsed ? format(parsed, "dd-MM") : "";
};

export const CustomDateFormate = (
  date: string | Date,
  dateFormat = getDisplayDateFormat()
): string => {
  const parsed = parseDate(date);
  return parsed ? format(parsed, dateFormat) : "";
};

export const CustomTimeFormate = (date: string | Date): string => {
  const parsed = parseDate(date);
  return parsed ? format(parsed, getDisplayTimeFormat()) : "";
};

export const CustomDateTimeFormate = (date: string | Date): string => {
  const parsed = parseDate(date);
  return parsed ? format(parsed, getDisplayDateTimeFormat()) : "";
};

export const GlobalDateFormate = (date: string | Date): string => {
  return CustomDateFormate(date);
};

export const SA_TIMEZONE = "Africa/Johannesburg";
export const SA_TIMEZONE_LABEL = "SAST (UTC+2)";

const getDateTimePart = (
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
): string => parts.find((part) => part.type === type)?.value ?? "";

const getTimeZoneOffsetMs = (utcTimestamp: number, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(utcTimestamp));

  const zonedAsUtc = Date.UTC(
    Number(getDateTimePart(parts, "year")),
    Number(getDateTimePart(parts, "month")) - 1,
    Number(getDateTimePart(parts, "day")),
    Number(getDateTimePart(parts, "hour")),
    Number(getDateTimePart(parts, "minute")),
    Number(getDateTimePart(parts, "second"))
  );

  return zonedAsUtc - utcTimestamp;
};

const parseNaiveDateTime = (
  value: string
): { year: number; month: number; day: number; hour: number; minute: number; second: number } | null => {
  const match = value.trim().match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );

  if (!match) {
    return null;
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4] ?? 0),
    minute: Number(match[5] ?? 0),
    second: Number(match[6] ?? 0),
  };
};

export const getSaDateTime = (
  date: Date = new Date()
): { date: string; time: string } => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: SA_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  return {
    date: `${getDateTimePart(parts, "year")}-${getDateTimePart(parts, "month")}-${getDateTimePart(parts, "day")}`,
    time: `${getDateTimePart(parts, "hour")}:${getDateTimePart(parts, "minute")}`,
  };
};

export const getSaCurrentDate = (): string => getSaDateTime().date;

export const getSaCurrentTime = (): string => getSaDateTime().time;

const SA_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Calendar date for "today" in South Africa, as a local Date at midnight. */
export const getSaTodayDate = (): Date => {
  const [year, month, day] = getSaCurrentDate().split("-").map(Number);
  return new Date(year, month - 1, day);
};

/** Label used in past-time validation, for example `06 Oct 2026, 18:45 SAST`. */
export const getSaNowLabel = (): string => {
  const { date, time } = getSaDateTime();
  const [year, month, day] = date.split("-");
  return `${day} ${SA_MONTHS[Number(month) - 1]} ${year}, ${time} SAST`;
};

export const isoWeekdayFromStorageDate = (date: string): number => {
  const parsed = parseDate(date);
  if (!parsed) {
    return 1;
  }
  const day = parsed.getDay();
  return day === 0 ? 7 : day;
};

export const addDaysToStorageDate = (date: string, days: number): string => {
  const parsed = parseDate(date);
  if (!parsed) {
    return date;
  }
  parsed.setDate(parsed.getDate() + days);
  return formatToStorageDate(parsed);
};

export const addMonthsToStorageDate = (date: string, months: number): string => {
  const parsed = parseDate(date);
  if (!parsed) {
    return date;
  }
  parsed.setMonth(parsed.getMonth() + months);
  return formatToStorageDate(parsed);
};

/** Add minutes to a South Africa wall-clock date and time without using the browser zone. */
export const addMinutesToWallClock = (
  date: string,
  time: string,
  minutesToAdd: number
): { date: string; time: string } => {
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day, hour || 0, minute || 0));
  utc.setUTCMinutes(utc.getUTCMinutes() + minutesToAdd);

  return {
    date: `${utc.getUTCFullYear()}-${pad(utc.getUTCMonth() + 1)}-${pad(utc.getUTCDate())}`,
    time: `${pad(utc.getUTCHours())}:${pad(utc.getUTCMinutes())}`,
  };
};

export const getSaNextHourTime = (): string => {
  const [hour, minute] = getSaCurrentTime().split(":").map(Number);
  const next = new Date(Date.UTC(2000, 0, 1, hour, minute));
  next.setUTCHours(next.getUTCHours() + 1);

  return `${pad(next.getUTCHours())}:${pad(next.getUTCMinutes())}`;
};

/**
 * Convert a South Africa wall-clock datetime to UTC `YYYY-MM-DD HH:mm:ss`.
 * Naive strings such as `2026-09-18T10:00` are treated as Africa/Johannesburg time.
 */
export const convertUTC = (
  date: Date | string,
  timeZone: string = SA_TIMEZONE
): string => {
  let utcDate: Date;

  if (date instanceof Date) {
    utcDate = date;
  } else {
    const value = String(date).trim();
    const hasExplicitOffset = /[zZ]$/.test(value) || /[+-]\d{2}:\d{2}$/.test(value);
    const parsed = hasExplicitOffset ? null : parseNaiveDateTime(value);

    if (parsed) {
      const utcGuess = Date.UTC(
        parsed.year,
        parsed.month - 1,
        parsed.day,
        parsed.hour,
        parsed.minute,
        parsed.second
      );
      utcDate = new Date(utcGuess - getTimeZoneOffsetMs(utcGuess, timeZone));
    } else {
      utcDate = new Date(value);
    }
  }

  if (Number.isNaN(utcDate.getTime())) {
    return "";
  }

  return utcDate.toISOString().slice(0, 19).replace("T", " ");
};
